import { useEffect, useRef, useState } from 'react';
import { Col, Modal, Row } from 'react-bootstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

import { StepperComponent } from '../../../../_metronic/assets/ts/components';
import { setMessage } from '../../../../store/alertMessage/actions';
import { updateMessage } from '../../messages/message-list/core/_requests';
import { useDispatch } from 'react-redux';
import { useQueryResponse } from './party-ledgers-list/core/QueryResponseProvider';
import { getPartyListingApi } from '../../messages/message-list/core/_requests';
import { setEditMode } from '../../../../store/editState/actions';

const modalsRoot = document.getElementById('root-modals') || document.body;

const EditPartyLedger = ({ show, handleClose, id, data }: any) => {
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const stepper = useRef<StepperComponent | null>(null);
  const amountRef = useRef<HTMLInputElement | null>(null);
  const remarksRef = useRef<HTMLTextAreaElement | null>(null);
    const remarksInputRef = useRef<any>(null)
  
  const [loading, setLoading] = useState<boolean>(false);
  const [initialData, setInitialData] = useState<any>(null);
  const [initialPartyLedgerList, setInitialPartyLedgerList] = useState<any[]>([]);
  const [senderPartyLedgerList, setSenderPartyLedgerList] = useState<any[]>([]);
  const [receiverPartyLedgerList, setReceiverPartyLedgerList] = useState<any[]>([]);
    const [charCount, setCharCount] = useState(70)
    const MAX_CHAR_LIMIT = 70;

  const dispatch = useDispatch();
  const intl = useIntl();
  const { refetch } = useQueryResponse();

  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(stepperRef.current as HTMLDivElement);
  };

  // Determine initial amount based on credit and debit, ensuring it's a number
  const getInitialAmount = () => {
    if (data?.credit === 0) {
      return data?.debit ? Math.abs(Number(data.debit)) : 0; // Convert to number
    } else if (data?.debit === 0) {
      return data?.credit ? Math.abs(Number(data.credit)) : 0;  // Convert to number
    }
    return 0; // Default fallback as number
  };

  // Updated MessageData with dynamic amount as a number
  const MessageData = {
    sender_id: data?.sender_id ? Number(data.sender_id) : 0, // Convert to number
    receiver_id: data?.receiver_id ? Number(data.receiver_id) : 0, // Convert to number
    amount: getInitialAmount(), // Already a number
    remarks: data?.remarks || '',
    date: data?.created_at || '',
  };

  const editMessageSchema = Yup.object().shape({
    amount: Yup.number()
      .required(`${intl.formatMessage({ id: 'AMOUNT.IS.REQUIRED' })}`)
      .test('not-zero', 'Amount cannot be zero', (value) => value !== 0)
      .typeError('Amount must be a number'),
    remarks: Yup.string(),
  });

  const formik = useFormik({
    initialValues: initialData || MessageData,
    enableReinitialize: true,
    validationSchema: editMessageSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      setLoading(true);
      try {
        if (id) {
          console.log('Submitting update with values:', values);
  
          // Use wasNegative from initialData (not from amount, since it's always positive)
          const wasNegative = initialData?.wasNegative;
          console.log("wasNegative at submit:", wasNegative);
  
          // Swap sender_id and receiver_id if wasNegative is true
          const modifiedValues = {
            sender_id: wasNegative ? Number(values.receiver_id) : Number(values.sender_id),
            receiver_id: wasNegative ? Number(values.sender_id) : Number(values.receiver_id),
            amount: Math.abs(Number(values.amount)), // Ensure amount is always positive
            remarks: values.remarks,
            date: values.date,
          };
  
          console.log('Calling updateMessage API with:', modifiedValues, id);
          const response = await updateMessage(modifiedValues, id);
          console.log('Update API response:', response);
  
          if (response?.message === "Remark updated successfully." || response?.message === "Normal entry updated successfully.") {
          setCharCount(70)

            dispatch(setMessage('success', response?.message ?? response?.responseMessage));
            
            // Dispatch the action to set edit mode to true, which will trigger clearing selected rows
            dispatch(setEditMode());
            
            handleClose();
            cancel(true);
            refetch();
          } else {
            dispatch(setMessage(
              'error',
              response?.responseData?.msg ? response?.responseData?.msg : response?.responseMessage
            ));
          }
        }
      } catch (error:any) {
        dispatch(setMessage('error', error?.response?.data?.message || "Entry updated by another user. Refresh and try again."));
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });
  
  

  const getPartyList = async () => {
    try {
      let response = (await getPartyListingApi()) as any;
      let responseData = response.data;

      const senderData = responseData.filter((item: any) => item.party_code === data?.sender_id);
      const receiverData = responseData.filter((item: any) => item.party_code === data?.receiver_id);

      formik.setFieldValue('sender_id', senderData[0]?.partyId ? Number(senderData[0].partyId) : Number(initialData?.sender_id));
      formik.setFieldValue('receiver_id', receiverData[0]?.partyId ? Number(receiverData[0].partyId) : Number(initialData?.receiver_id));

      if (responseData) {
        let partyLedger = responseData.map((x: any) => {
          return {
            label: x.party_name,
            value: Number(x.uuid), // Convert uuid to number
            ...x,
          };
        });
        partyLedger = [{ value: null, label: 'Select..' }, ...partyLedger];

        setInitialPartyLedgerList(partyLedger);
        setSenderPartyLedgerList(partyLedger);
        setReceiverPartyLedgerList(partyLedger);
      } else {
        setInitialPartyLedgerList([]);
        setSenderPartyLedgerList([]);
        setReceiverPartyLedgerList([]);
      }
    } catch (err: any) {
      let message = err?.message
        ? err.message
        : err?.responseMessage
        ? err.responseMessage
        : ('Something went wrong' as any);
      dispatch(setMessage('error', message));
    }
  };

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch();
    }
  };

  // useEffect(() => {
  //   if (show && id && data) {
  //     console.log('Modal opened, setting initial data from props:', data);
  //     setInitialData({
  //       sender_id: data?.sender_id ? Number(data.sender_id) : 0, // Convert to number
  //       receiver_id: data?.receiver_id ? Number(data.receiver_id) : 0, // Convert to number
  //       amount: getInitialAmount(), // Already a number
  //       remarks: data?.remarks || '',
  //       date: data?.created_at || '',
  //     });
  //   }
  // }, [show, id, data]);
  useEffect(() => {
    if (show && id && data) {
      amountRef.current?.focus(); // Focus on amount field
      const wasNegative = (data.debit && Number(data.debit) < 0) || (data.credit && Number(data.credit) < 0);
      const remarks = data?.remarks || '';
      setCharCount(MAX_CHAR_LIMIT - remarks.length); // Initialize char count
      setInitialData({
        sender_id: wasNegative ? Number(data.receiver_id) : Number(data.sender_id),
        receiver_id: wasNegative ? Number(data.sender_id) : Number(data.receiver_id),
        amount: getInitialAmount(),
        remarks,
        date: data?.created_at || '',
        wasNegative,
      });
      setTimeout(() => {
        if (remarksInputRef.current) {
          const len = remarksInputRef.current.value.length;
          remarksInputRef.current.focus();
          remarksInputRef.current.setSelectionRange(len, len); // place cursor at end
        }
      }, 0);
    }
  }, [show, id, data]);
  


  useEffect(() => {
    if (show && initialData) {
      console.log('Fetching party list after initial data is loaded');
      getPartyList();
    }
  }, [show, initialData]);

  const handleModalClose = () => {
    // Reset formik values to initial data
    if (initialData) {
      formik.resetForm({ values: initialData });
    }
    handleClose();
  };

  return createPortal(
    <Modal
      id='kt_modal_create_app'
      tabIndex={-1}
      aria-hidden='true'
      dialogClassName='modal-dialog modal-dialog-centered mw-650px'
      show={show}
      onHide={handleModalClose}
      onEntered={() => {
        loadStepper();
        amountRef.current?.focus();
      }}
      backdrop='static'
    >
      <Modal.Header closeButton>
        <h2>Edit Message</h2>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <form
            id='kt_modal_add_message_form'
            className='form'
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              console.log('Form submitted');
              formik.handleSubmit();
            }}
          >
            <Row className='mb-8'>
              {/* <Col lg={6}>
                <label className='fw fs-6 mb-2'>SENDER ID</label>
                <h2 className='fw-bold'>{formik.values.sender_id}</h2>
              </Col> */}
              <Col lg={6}>
                <label className='fw fs-6 mb-2'>RECEIVER</label>
                <h2 className='fw-bold'>{data.receiver}</h2>
              </Col>
            </Row>
            <Row className='mb-8'>
  <Col lg={6}>
    <label className='required fw fs-6 mb-2'>AMOUNT</label>
    <div className='form-group mb-3'>
      <input
        placeholder='Amount'
        type='text'
        name='amount'
        ref={amountRef}
        className='form-control mb-2'
        onChange={(e: any) => {
          let value = e.target.value;
          if (value.length > 12) {
            value = value.slice(0, 12);
          }

          const regex = /^\d*$/;
          if (regex.test(value)) {
            formik.setFieldValue('amount', value);
          }
        }}
        value={formik.values.amount === 0 ? '' : formik.values.amount}
        // ref={amountInputRef}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            remarksInputRef.current?.focus();
          }
        }}
        onPaste={(e) => {
          e.preventDefault();
          let pastedText = e.clipboardData.getData('Text').replace(/[^0-9]/g, '');
          const numericValue = pastedText === '' ? 0 : Number(pastedText);

          if (String(Math.abs(numericValue)).length <= 12) {
            formik.setFieldValue('amount', numericValue);
          }
        }}
      />
      {/* {formik.touched.amount && formik.errors.amount ? (
        // <div className='text-danger'>{formik.errors.amount}</div>
      ) : null} */}
    </div>
  </Col>
</Row>
<Row className='mb-8'>
  <Col lg={12}>
    <label className='fw fs-6 mb-2'>REMARKS</label>
    <textarea
      placeholder={intl.formatMessage({ id: 'NARRATION' })}
      name='remarks'
      ref={remarksInputRef}
      maxLength={MAX_CHAR_LIMIT}
      rows={3}
      className='form-control mb-3'
      onChange={(e: any) => {
        const inputValue = e.target.value;
        setCharCount(MAX_CHAR_LIMIT - inputValue.length); // update remaining characters
        formik.setFieldValue('remarks', inputValue.trimStart());
      }}
      value={formik?.values?.remarks}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !loading) {
          e.preventDefault();
          formik.handleSubmit();
        }
      }}
    />
    <div className='text-end'>
            <small className={charCount <= 10 ? 'text-danger' : ''}>
              Remaining characters: {charCount}
            </small>
          </div>
    {formik.touched.remarks && formik.errors.remarks && (
      <div className='fv-plugins-message-container text-danger'>
        <span role='alert'>{formik.errors.remarks as any}</span>
      </div>
    )}
  </Col>
</Row>



            <div className='text-end pe-4 mb-6 pt-5'>
              <button
                type='submit'
                className='btn btn-primary me-3 mb-3 mb-md-0'
                disabled={loading}
                onClick={() => console.log('Submit button clicked')}
              >
                <span className='indicator-label'>
                  {loading ? 'Please Wait...' : intl.formatMessage({ id: 'SUBMIT' })}
                </span>
              </button>

              <button
                type='reset'
                onClick={handleModalClose}
                className='btn btn-light me-3 mb-3 mb-md-0'
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal.Body>
    </Modal>,
    modalsRoot
  );
};

export default EditPartyLedger;